/**
 * Verhalten der ChiDrops CLEANSE VSL-Salespage.
 *
 * Zwei Teile:
 *   1. FAQ-Akkordeon und die einfahrende Sticky-CTA-Leiste. Das lag in LanderLab
 *      im Custom-Code-Feld der Variante und nicht im HTML, deshalb steht es hier.
 *   2. Add-to-Cart fuer die drei Pakete. Laeuft ueber denselben Weg wie der Rest
 *      des Shops: POST auf Theme.routes.cart_add_url, danach ein CartAddEvent.
 *      Darauf hoeren der Drawer (oeffnet sich) und die Cart-Bubble (zaehlt hoch).
 */

import { CartAddEvent } from '@theme/events';

/* ------------------------------------------------------------------ Akkordeon */

function initAccordion() {
  document.querySelectorAll('button.flex.w-full.items-center.justify-between').forEach((btn) => {
    const panel = btn.nextElementSibling;
    if (!panel || !panel.classList.contains('overflow-hidden')) return;

    const svg = btn.querySelector('svg');

    const set = (open) => {
      panel.style.maxHeight = open ? `${panel.scrollHeight}px` : '0px';
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (!svg) return;

      // Das Plus wird zum Minus, indem der senkrechte Strich verschwindet.
      const bar = svg.querySelector('path[data-plus]');
      if (open) {
        bar?.remove();
      } else if (!bar) {
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', 'M12 5v14');
        path.setAttribute('data-plus', '1');
        svg.appendChild(path);
      }
    };

    set(false);
    btn.addEventListener('click', () => set(panel.style.maxHeight === '0px'));
  });
}

/* --------------------------------------------------------------- Sticky-Leiste */

/** Ab wieviel Prozent Scrolltiefe die Leiste einfaehrt. */
const STICKY_BAR_SCROLL_RATIO = 0.55;

function initStickyBar() {
  const bar = document.querySelector('.fixed.inset-x-0.bottom-0.z-50');
  if (!bar) return;

  bar.style.willChange = 'transform';
  const show = (on) => {
    bar.style.transform = on ? 'translateY(0px)' : 'translateY(110%)';
  };
  show(false);

  /**
   * Scrolltiefe als Anteil der scrollbaren Strecke, nicht als fester Pixelwert:
   * die Seite ist auf dem Handy deutlich laenger als auf dem Desktop.
   * @returns {number} 0 bis 1
   */
  const scrollRatio = () => {
    const doc = document.documentElement;
    const top = window.pageYOffset || doc.scrollTop || 0;
    const scrollable = (doc.scrollHeight || 0) - window.innerHeight;
    return scrollable > 0 ? top / scrollable : 0;
  };

  window.addEventListener('scroll', () => show(scrollRatio() >= STICKY_BAR_SCROLL_RATIO), {
    passive: true,
  });
}

/* ------------------------------------------------------------------ Add to cart */

/** @returns {string} kommaseparierte Section-IDs, die der Server mitrendern soll */
function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
    .filter(Boolean)
    .join(',');
}

async function addToCart(link) {
  const variantId = Number(link.dataset.cdAtc);
  if (!variantId) return;

  link.setAttribute('aria-busy', 'true');

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: cartSectionIds(),
      }),
    }).then((r) => r.json());

    // Shopify liefert bei Fehlern ein `status`-Feld statt des Warenkorbs.
    if (response.status) {
      console.error('[cd-vsl] Add to cart fehlgeschlagen:', response.description || response.message);
      window.location.href = link.getAttribute('href');
      return;
    }

    const cart = await fetch(`${Theme.routes.cart_url}.js`)
      .then((r) => r.json())
      .catch(() => undefined);

    document.dispatchEvent(
      new CartAddEvent(cart, link.id, {
        source: 'cd-vsl-page',
        itemCount: 1,
        variantId: String(variantId),
        sections: response.sections,
      })
    );

    // Der Drawer oeffnet sich normalerweise selbst (auto_open_cart_drawer).
    // Falls die Einstellung mal aus ist, hier nachhelfen — der Kauf soll auf
    // dieser Seite immer im Warenkorb landen, sichtbar.
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && !drawer.querySelector('dialog')?.open) {
      customElements.whenDefined('cart-drawer-component').then(() => drawer.open?.());
    }
  } catch (error) {
    console.error('[cd-vsl] Add to cart fehlgeschlagen:', error);
    // Ohne JS-Pfad bleibt der href als Fallback: /cart/<variant>:1
    window.location.href = link.getAttribute('href');
  } finally {
    link.removeAttribute('aria-busy');
  }
}

function initAddToCart() {
  document.querySelectorAll('a[data-cd-atc]').forEach((link) => {
    link.addEventListener('click', (event) => {
      event.preventDefault();
      addToCart(link);
    });
  });
}

/* ------------------------------------------------------------------------ Start */

function init() {
  initAccordion();
  initStickyBar();
  initAddToCart();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
